// Uso del panel, usuario por usuario. Es la pregunta de admin que ninguna
// pantalla contestaba: quién entra, qué tanto, y quién no ha vuelto.
//
// DOS SEÑALES, PORQUE UNA SOLA MIENTE:
//   - `userActivity` (utils/presence.js): un doc por usuario y hora, así que
//     contarlos da HORAS con el panel abierto. Cubre al que entra a mirar
//     su calendario y no toca nada — que es la mayoría del uso real.
//   - `bitacora`: las acciones que sí cambiaron algo. Es el uso con
//     consecuencias, y sin él "estuvo conectado 40 horas" no dice si
//     trabajó o dejó la pestaña abierta.
// La última actividad es el máximo de las dos.
//
// OJO CON LOS ADMINS: presence y bitácora los excluyen a propósito (modo
// sigiloso — el staff entrando a un venue no es actividad del venue). Un
// admin SIEMPRE va a salir sin actividad aquí, y eso no significa que no
// use el panel. Por eso viajan marcados con `esAdmin` y la UI lo dice.

// Los únicos roles que pueden entrar al panel (espejo de ALLOWED_ROLES en
// AuthProvider). `client` y `planner` existen en `users` pero son cuentas
// del homesite: meterlas aquí llenaría el reporte de cientos de personas
// que nunca van a entrar porque no tienen dónde.
export const ROLES_DEL_PANEL = ["admin", "owner", "viewer", "inversionista"];

export const usaElPanel = (rol) => ROLES_DEL_PANEL.includes(rol);

export const ESTADOS_USO = [
  { key: "activo", label: "Activo", dias: 7 },
  { key: "tibio", label: "Intermitente", dias: 30 },
  { key: "inactivo", label: "Inactivo", dias: Infinity },
  { key: "nunca", label: "Nunca entró", dias: null },
];

const ms = (v) => {
  if (!v) return 0;
  if (v.toDate) return v.toDate().getTime();
  if (v.seconds) return v.seconds * 1000;
  const t = new Date(v).getTime();
  return isNaN(t) ? 0 : t;
};

// La familia de una acción de bitácora: el prefijo antes del punto
// ("event.create" → "event").
export const familiaDeAccion = (accion) =>
  accion ? (accion.includes(".") ? accion.split(".")[0] : accion) : null;

// Las 2-3 secciones en las que más trabaja una persona, ya ordenadas.
const actividadPrincipal = (familias = {}) =>
  Object.entries(familias)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([familia, total]) => ({ familia, total }));

export function estadoDeUso(diasDesde) {
  if (diasDesde == null) return "nunca";
  if (diasDesde <= 7) return "activo";
  if (diasDesde <= 30) return "tibio";
  return "inactivo";
}

// `actividad` y `bitacora` son los docs crudos ya acotados a la ventana por
// quien consulta; `bitacora` trae `venueId` resuelto del padre por el
// llamador (la subcolección no lo guarda).
export function usoPorUsuario({
  users = [],
  actividad = [],
  bitacora = [],
  venues = [],
  ahora = new Date(),
}) {
  const ahoraMs = ahora.getTime();
  const nombreVenue = {};
  (venues || []).forEach((v) => {
    if (v?.id) nombreVenue[v.id] = v.name || v.nombre || v.id;
  });

  const acc = {};
  const deUsuario = (userId) =>
    acc[userId] ||
    (acc[userId] = {
      userId,
      horas: 0,
      acciones: 0,
      ultimaMs: 0,
      venueIds: new Set(),
      // Fallbacks para el usuario que ya no existe en `users` (cuenta
      // borrada) pero dejó rastro: sin esto sería una fila anónima.
      nombreRastro: "",
      correoRastro: "",
      rolRastro: "",
      familias: {},
    });

  (actividad || []).forEach((a) => {
    if (!a?.userId) return;
    const u = deUsuario(a.userId);
    // Un doc = una hora con el panel abierto (el id es determinista por
    // hora, así que multi-pestaña no infla la cuenta).
    u.horas += 1;
    u.ultimaMs = Math.max(u.ultimaMs, ms(a.lastSeen));
    if (a.venueId) u.venueIds.add(a.venueId);
    u.nombreRastro = u.nombreRastro || a.userName || "";
    u.correoRastro = u.correoRastro || a.userEmail || "";
    u.rolRastro = u.rolRastro || a.role || "";
  });

  (bitacora || []).forEach((b) => {
    if (!b?.userId) return;
    const u = deUsuario(b.userId);
    u.acciones += 1;
    u.ultimaMs = Math.max(u.ultimaMs, ms(b.timestamp));
    if (b.venueId) u.venueIds.add(b.venueId);
    u.nombreRastro = u.nombreRastro || b.userName || "";
    const familia = familiaDeAccion(b.action);
    if (familia) u.familias[familia] = (u.familias[familia] || 0) + 1;
  });

  const conocidos = new Set();
  // Solo cuentas que pueden entrar al panel: un `client` del homesite no
  // "dejó de usar" nada.
  const delPanel = (users || []).filter((u) => usaElPanel(u?.role));
  const filas = delPanel.map((user) => {
    conocidos.add(user.id);
    const u = acc[user.id];
    const esAdmin = user.role === "admin";
    const ultimaMs = u?.ultimaMs || 0;
    const diasDesde = ultimaMs
      ? Math.floor((ahoraMs - ultimaMs) / 86400000)
      : null;
    // Los venues del usuario mandan sobre los que aparezcan en su rastro:
    // un venue que ya no tiene asignado no es suyo aunque lo haya visitado.
    const venueIds = Array.isArray(user.venues) && user.venues.length > 0
      ? user.venues
      : Array.from(u?.venueIds || []);
    return {
      id: user.id,
      nombre: user.name || u?.nombreRastro || "",
      correo: user.email || u?.correoRastro || "",
      rol: user.role || u?.rolRastro || "",
      esAdmin,
      horas: u?.horas || 0,
      acciones: u?.acciones || 0,
      ultima: ultimaMs ? new Date(ultimaMs) : null,
      diasDesde,
      estado: estadoDeUso(diasDesde),
      venues: venueIds.map((id) => nombreVenue[id] || id),
      venueIds,
      actividadPrincipal: actividadPrincipal(u?.familias),
      // Sin datos y sin explicación posible: para un admin el vacío es
      // esperado, para un owner es el hallazgo.
      sinDatosEsperado: esAdmin,
    };
  });

  // Rastro de cuentas que ya no están en `users`: se ven, pero aparte.
  const huerfanos = Object.values(acc)
    .filter((u) => !conocidos.has(u.userId))
    .filter((u) => !u.rolRastro || usaElPanel(u.rolRastro))
    .map((u) => ({
      id: u.userId,
      nombre: u.nombreRastro || "(cuenta eliminada)",
      correo: u.correoRastro || "",
      rol: u.rolRastro || "",
      esAdmin: false,
      horas: u.horas,
      acciones: u.acciones,
      ultima: u.ultimaMs ? new Date(u.ultimaMs) : null,
      diasDesde: u.ultimaMs
        ? Math.floor((ahoraMs - u.ultimaMs) / 86400000)
        : null,
      estado: estadoDeUso(
        u.ultimaMs ? Math.floor((ahoraMs - u.ultimaMs) / 86400000) : null
      ),
      venues: Array.from(u.venueIds).map((id) => nombreVenue[id] || id),
      venueIds: Array.from(u.venueIds),
      actividadPrincipal: actividadPrincipal(u.familias),
      sinDatosEsperado: false,
      huerfano: true,
    }));

  const todas = [...filas, ...huerfanos].sort(
    (a, b) =>
      (b.ultima?.getTime() || 0) - (a.ultima?.getTime() || 0) ||
      b.horas - a.horas ||
      (a.nombre || "").localeCompare(b.nombre || "")
  );

  // Los totales se cuentan SIN admins: son los que el panel no mide.
  const medibles = todas.filter((f) => !f.esAdmin);
  const cuenta = (estado) => medibles.filter((f) => f.estado === estado).length;

  return {
    filas: todas,
    totales: {
      usuarios: todas.length,
      medibles: medibles.length,
      activos: cuenta("activo"),
      tibios: cuenta("tibio"),
      inactivos: cuenta("inactivo"),
      nunca: cuenta("nunca"),
      horas: medibles.reduce((s, f) => s + f.horas, 0),
      acciones: medibles.reduce((s, f) => s + f.acciones, 0),
    },
  };
}

// Qué hace la gente cuando entra: las acciones de bitácora agrupadas por su
// familia ("event", "payment", "costo"…), que es el prefijo antes del punto.
export function usoPorAccion(bitacora = []) {
  const map = {};
  (bitacora || []).forEach((b) => {
    const accion = b?.action || "";
    const familia = familiaDeAccion(accion) || "otro";
    const acc = map[familia] || (map[familia] = { familia, total: 0, acciones: {} });
    acc.total += 1;
    acc.acciones[accion] = (acc.acciones[accion] || 0) + 1;
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
}
